import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useOrganizationStore } from '../../store/organization.store';
import { useAuthStore } from '../../store/auth.store';
import * as ROUTES from '../../constants/routes';

/**
 * Higher-order component that ensures the user has an organization selected.
 * If no organization is selected, redirects to the appropriate page:
 * - GET_STARTED if user has no organizations
 * - SELECT_ORGANIZATION if user has organizations but none selected
 */
const withOrganization = (Component: React.ComponentType<any>) => {
  const WithOrganization: React.FC<any> = (props) => {
    const history = useHistory();
    const auth = useAuthStore(state => state.auth);
    const organizations = useOrganizationStore(state => state.organizations);
    const currentOrgId = useOrganizationStore(state => state.currentOrgId);
    const loading = useOrganizationStore(state => state.isLoading);

    useEffect(() => {
      // Wait for auth and org loading to complete
      if (!auth || loading) return;

      // If user has no organizations, send them to create/join one
      if (organizations.length === 0) {
        history.push(ROUTES.GET_STARTED);
        return;
      }

      // If user has organizations but none selected, let them choose
      if (!currentOrgId) {
        history.push(ROUTES.SELECT_ORGANIZATION);
        return;
      }
    }, [auth, organizations, currentOrgId, loading, history]);

    // If loading, wait
    if (loading) {
      return null;
    }

    // If not authenticated, redirect to sign in
    if (!auth) {
      history.push(ROUTES.SIGN_IN);
      return null;
    }

    // Don't render if no orgs (will redirect)
    if (organizations.length === 0) {
      return null;
    }

    // Don't render if no org selected (will redirect)
    if (!currentOrgId) {
      return null;
    }

    return <Component {...props} />;
  };

  WithOrganization.displayName = `WithOrganization(${Component.displayName || Component.name || 'Component'})`;

  return WithOrganization;
};

export default withOrganization;
